import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, Trophy, Flame, Play } from "lucide-react";
import AppLayout from "../components/AppLayout";
import CourseCard from "../components/CourseCard";
import { courses, weeklyHours } from "../data/mockData";
import { useAuth } from "../contexts/AuthContext";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Dashboard() {
    const { user } = useAuth();
    const firstName = user?.name ? user.name.split(" ")[0] : "Welcome";
    const enrolled = courses.filter((c) => c.enrolled);
    const recommended = courses.filter((c) => !c.enrolled).slice(0, 3);
    const continueCourse = enrolled[0];

    const hour = new Date().getHours();
    const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

    const stats = [
        { label: "Hours learned", value: "78.4", icon: Clock },
        { label: "Courses in progress", value: enrolled.length, icon: Play },
        { label: "Certificates earned", value: "3", icon: Trophy },
        { label: "Day streak", value: "12", icon: Flame },
    ];

    return (
        <AppLayout searchPlaceholder="Search courses, lessons...">
            {/* Welcome */}
            <div style={{ marginBottom: 40 }}>
                <div style={{ fontSize: 13, color: "#6b7280" }}>{greeting},</div>
                <h1 style={{ fontSize: "clamp(36px,6vw,64px)", fontWeight: 900, letterSpacing: "-0.04em", lineHeight: 1.1, marginTop: 4 }}>
                    {firstName} <span style={{ color: "#7C5CFF" }}>.</span>
                </h1>
            </div>

            {/* Stats */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 32 }}>
                {stats.map((s, i) => {
                    const Icon = s.icon;
                    return (
                        <div key={i} data-testid={`stat-${i}`} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 20 }}>
                            <Icon size={18} color={i % 2 === 0 ? "#CFFF50" : "#7C5CFF"} style={{ marginBottom: 12 }} />
                            <div style={{ fontSize: 30, fontWeight: 900 }}>{s.value}</div>
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{s.label}</div>
                        </div>
                    );
                })}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 300px", gap: 20, marginBottom: 40 }}>
                {/* Continue Learning */}
                {continueCourse && (
                    <div style={{ position: "relative", overflow: "hidden", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, padding: 28 }}>
                        <div style={{ position: "absolute", inset: 0, opacity: 0.25 }}>
                            <img src={continueCourse.cover} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, #0A0B0E 40%, transparent)" }} />
                        </div>
                        <div style={{ position: "relative" }}>
                            <div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#CFFF50", fontWeight: 700, marginBottom: 8 }}>Continue learning</div>
                            <h2 style={{ fontSize: "clamp(20px,3vw,30px)", fontWeight: 900, letterSpacing: "-0.02em", maxWidth: 400, lineHeight: 1.2, marginBottom: 16 }}>{continueCourse.title}</h2>
                            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "#9ca3af", marginBottom: 20 }}>
                                <span>{continueCourse.instructor?.name}</span>
                                <span style={{ color: "#374151" }}>•</span>
                                <span>{continueCourse.durationHours}h total</span>
                            </div>
                            <div style={{ maxWidth: 400, marginBottom: 24 }}>
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 6 }}>
                                    <span style={{ color: "#9ca3af" }}>Lesson 4 of 8</span>
                                    <span style={{ color: "#CFFF50", fontWeight: 700 }}>{continueCourse.progress}%</span>
                                </div>
                                <div style={{ height: 6, borderRadius: 99, background: "rgba(255,255,255,0.08)", overflow: "hidden" }}>
                                    <div style={{ height: "100%", borderRadius: 99, background: "linear-gradient(90deg, #7C5CFF, #CFFF50)", width: `${continueCourse.progress}%` }} />
                                </div>
                            </div>
                            <Link to={`/learn/${continueCourse.id}`} data-testid="continue-learning-btn" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "12px 24px", borderRadius: 99, background: "#CFFF50", color: "#0A0B0E", fontWeight: 800, fontSize: 14, textDecoration: "none" }}>
                                Resume <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>
                )}

                {/* Weekly chart */}
                <div style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 24, padding: 24 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <h3 style={{ fontSize: 16, fontWeight: 800 }}>Weekly hours</h3>
                        <span style={{ fontSize: 11, color: "#6b7280" }}>Last 8 days</span>
                    </div>
                    <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 16 }}>Total: <span style={{ color: "#CFFF50", fontWeight: 700 }}>34.8 hours</span></p>
                    <div style={{ height: 180 }}>
                        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
                            <BarChart data={weeklyHours} barCategoryGap={8}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                                <XAxis dataKey="day" tick={{ fill: "#9CA3AF", fontSize: 10 }} axisLine={false} tickLine={false} />
                                <YAxis hide />
                                <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} contentStyle={{ background: "#0E0F12", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff", fontSize: 12 }} />
                                <Bar dataKey="completed" stackId="a" fill="#7C5CFF" radius={[0, 0, 0, 0]} />
                                <Bar dataKey="inProgress" stackId="a" fill="#CFFF50" radius={[6, 6, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                    <div style={{ marginTop: 12, display: "flex", gap: 16, fontSize: 11, color: "#9ca3af" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#7C5CFF" }} /> Completed</span>
                        <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 8, height: 8, borderRadius: "50%", background: "#CFFF50" }} /> In progress</span>
                    </div>
                </div>
            </div>

            {/* In Progress */}
            {enrolled.length > 0 && (
                <div style={{ marginBottom: 40 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                        <h2 style={{ fontSize: 22, fontWeight: 800 }}>In progress</h2>
                        <Link to="/my-courses" data-testid="view-all-my-courses" style={{ fontSize: 13, color: "#CFFF50", fontWeight: 700, textDecoration: "none" }}>View all</Link>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
                        {enrolled.map((c) => <CourseCard key={c.id} course={c} showProgress />)}
                    </div>
                </div>
            )}

            {/* Recommended */}
            <div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>Recommended for you</h2>
                        <p style={{ fontSize: 13, color: "#6b7280" }}>Based on your training profile</p>
                    </div>
                    <Link to="/catalog" data-testid="explore-catalog" style={{ fontSize: 13, color: "#CFFF50", fontWeight: 700, textDecoration: "none" }}>Explore catalog</Link>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
                    {recommended.map((c) => <CourseCard key={c.id} course={c} />)}
                </div>
            </div>
        </AppLayout>
    );
}
